import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  WinePreferenceData,
  WinePreferenceSchema,
} from "@/types/forms/preferences/winePreference.form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createColumnHelper } from "@tanstack/react-table";
import { winePreference } from "@/types/api/api-types";
import TrashOutlineIcon from "@/assets/icons/trash-outline.svg?react";
import EditOutlineIcon from "@/assets/icons/edit-outline.svg?react";
import StyledTable from "@/components/styled/StyledTable";
import {
  useCreateWinePreference,
  useDeleteWinePreference,
  useUpdateWinePreference,
} from "@/api/preference.api";
import { useState } from "react";
import StyledInput from "@/components/styled/StyledInput";
import { StyledSelectSingleValue } from "@/components/styled/StyledSelectSingleValue";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { parseEnum } from "@/lib/enums.util";
import { volumesOptions } from "@/constants/options/volumes.option";
import { StyledSelectSingleValueInt } from "@/components/styled/StyledSelectSingleValueInt";
import { StyledInputStep } from "@/components/styled/StyledInputStep";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useWineColorOptions } from "@/hooks/useEnumOptions";

const columnHelper = createColumnHelper<winePreference>();

type PreferencesProductProps = {
  pref: winePreference[];
};

export const PreferencesProduct = ({ pref }: PreferencesProductProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const wineColorOptions = useWineColorOptions();
  const newWinePreference = useForm<WinePreferenceData>({
    defaultValues: {
      wineName: "",
      year: undefined,
      alcoholPercent: undefined,
      volume: undefined,
      color: undefined,
      unitPrice: undefined,
    },
    resolver: zodResolver(WinePreferenceSchema),
    mode: "onChange",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [currId, setCurrId] = useState<string | null>(null);
  const { mutateAsync: createWinePreference } = useCreateWinePreference();
  const { mutateAsync: updateWinePreference } = useUpdateWinePreference();
  const { mutateAsync: deleteWinePreference } = useDeleteWinePreference();

  const handleDeleteWinePreference = async (id: string) => {
    deleteWinePreference(id);
  };

  const handleModifyWinePreference = async (row: winePreference) => {
    if (editing) return;
    newWinePreference.setValue("alcoholPercent", row.alcoholPercent);
    newWinePreference.setValue("volume", row.volume);
    newWinePreference.setValue("wineName", row.wineName);
    newWinePreference.setValue("year", row.year);
    newWinePreference.setValue("color", parseEnum(WineColorEnum, row.color) ?? WineColorEnum.ROUGE);
    newWinePreference.setValue("unitPrice", row.unitPrice);
    setCurrId(row.id);
    setEditing(true);
  };

  const handleCancelEditing = () => {
    setEditing(false);
    setCurrId(null);
    newWinePreference.reset();
  };

  const onSubmit: SubmitHandler<WinePreferenceData> = async (data) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      if (currId) {
        await updateWinePreference({
          ...data,
          id: currId,
        });
      } else {
        await createWinePreference(data);
      }
      setEditing(false);
      setCurrId(null);
      newWinePreference.reset();
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    columnHelper.display({
      id: "wineName",
      header: t("profilePreferencesProduct.columns.wineName"),
      cell: (info) => info.row.original.wineName,
    }),
    columnHelper.display({
      id: "year",
      header: t("profilePreferencesProduct.columns.year"),
      cell: (info) => info.row.original.year,
    }),
    columnHelper.display({
      id: "alcoholPercent",
      header: t("profilePreferencesProduct.columns.alcoholPercent"),
      cell: (info) => info.row.original.alcoholPercent + "%",
    }),
    columnHelper.display({
      id: "volume",
      header: t("profilePreferencesProduct.columns.volume"),
      cell: (info) => info.row.original.volume + " L",
    }),
    columnHelper.display({
      id: "color",
      header: t("profilePreferencesProduct.columns.color"),
      cell: (info) => wineColorOptions.find((el) => el.value === info.row.original.color)?.label,
    }),
    columnHelper.display({
      id: "price",
      header: t("profilePreferencesProduct.columns.price"),
      cell: (info) => info.row.original.unitPrice + "€",
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (data) => (
        <div className="flex gap-6">
          <TrashOutlineIcon
            className="cursor-pointer text-slate-600 hover:text-slate-800"
            onClick={() => handleDeleteWinePreference(data.row.original.id)}
          />
          <EditOutlineIcon
            className={`${!editing ? "cursor-pointer text-slate-600 hover:text-slate-800" : "cursor-default text-slate-400"} `}
            onClick={() => handleModifyWinePreference(data.row.original)}
          />
        </div>
      ),
    }),
  ];

  return (
    <div className="flex flex-col gap-6 lg:px-8">
      <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
        <div className="items-left flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("profilePreferencesProduct.title")}</h2>
          <p className="text-sm text-gray-600">{t("profilePreferencesProduct.subtitle")}</p>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {/* <h3 className="text-base font-medium text-black">Bouteilles</h3> */}
        <StyledTable data={pref} columns={columns} />
        {editing && (
          <form
            onSubmit={newWinePreference.handleSubmit(onSubmit)}
            className="mb-20 flex flex-col gap-4 rounded-lg border bg-white p-4"
          >
            <div className="flex w-full flex-col gap-2 md:flex-row">
              <StyledInput
                title={t("profilePreferencesProduct.wineNameLabel")}
                control={newWinePreference.control}
                name="wineName"
                className="flex-1"
              />
              <StyledInput
                title={t("profilePreferencesProduct.yearLabel")}
                control={newWinePreference.control}
                name="year"
                type="number"
                className="flex-1"
              />
              <StyledInputStep
                title={t("profilePreferencesProduct.alcoholPercentLabel")}
                control={newWinePreference.control}
                name="alcoholPercent"
                type="number"
                className="flex-1"
              />
              <StyledSelectSingleValueInt
                options={volumesOptions}
                setValue={(e: any) => newWinePreference.setValue("volume", e)}
                title={t("profilePreferencesProduct.volumeLabel")}
                control={newWinePreference.control}
                name="volume"
                className="flex-1"
              />
              <StyledSelectSingleValue
                options={wineColorOptions}
                setValue={(e: any) => newWinePreference.setValue("color", e)}
                title={t("profilePreferencesProduct.colorLabel")}
                control={newWinePreference.control}
                name="color"
                className="flex-1"
              />
              <StyledInputStep
                title={t("profilePreferencesProduct.unitPriceLabel")}
                control={newWinePreference.control}
                name="unitPrice"
                className="flex-1"
              />
            </div>
            <div className="flex flex-row gap-4">
              <button
                disabled={isLoading}
                className={`${isLoading ? "bg-gray-40 cursor-wait" : "bg-brand50"} text-brand700 hover:border-brand600 hover:text-brand700 w-fit rounded-lg px-4 py-3 text-sm font-semibold hover:border`}
              >
                {isLoading ? (
                  <StyledSpinner width="20" height="20" />
                ) : (
                  t("profilePreferencesProduct.submit")
                )}
              </button>
              <button
                onClick={handleCancelEditing}
                className="w-fit rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:border-black hover:text-black"
              >
                {t("common:actions.cancel")}
              </button>
            </div>
          </form>
        )}
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="bg-brand50 text-brand700 hover:border-brand600 hover:text-brand700 w-fit rounded-lg px-4 py-3 text-sm font-semibold hover:border"
          >
            {t("profilePreferencesProduct.addBottle")}
          </button>
        )}
      </div>
    </div>
  );
};
